import { map, finalize, catchError, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { DataServiceBase } from '../../../core/service/base/data.service.base';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { LeaseAgreementDocument } from '../../model/lease-agreement-document';

@Injectable()
export class LeaseAgreementDocumentService extends DataServiceBase {
  constructor(client: HttpClient, messageService: MessageService, progressBar: ProgressBarService, @Inject(APP_CONFIG) config: IAppConfig) {
    super(client, messageService, progressBar, config, 'lease-agreement/documents');
  }

  add(model: LeaseAgreementDocument): Observable<any> {
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(model));
    formData.append('document', model.document, model.document.name);
    this.progressBar.start();
    return this.client.post(this.endpoint, formData).pipe(
      tap(() => this.notification.showSuccess()),
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  requestESignature(id: string): Observable<any> {
    return this.client
      .post<any>(`${this.endpoint}/request-signature`, JSON.stringify({ leaseAgreementDocumentId: id }), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        })
      );
  }

  delete(id: string): Observable<void> {
    this.progressBar.start();
    return this.client.delete<void>(`${this.endpoint}/${id}`).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  changeVisibilityLevel(form: any): Observable<LeaseAgreementDocument | any> {
    return this.client
      .post<LeaseAgreementDocument>(`${this.endpoint}/change-visibility`, JSON.stringify(form), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw new Error();
        }),
        tap(() => this.notification.showSuccess('SUCCESS.DOCUMENT_VISIBILITY_CHANGED'))
      );
  }

  download(model: LeaseAgreementDocument): Observable<File> {
    this.progressBar.start();
    return this.getFile(`${this.endpoint}/download/${model.id}`, 'document').pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  getByLeaseAgreement(leaseAgreementId: string): Observable<LeaseAgreementDocument[]> {
    this.progressBar.start();
    return this.client.get<LeaseAgreementDocument[]>(`${this.endpoint}/${leaseAgreementId}`).pipe(
      map(items => {
        if (Array.isArray(items)) {
          return items.map(m => Object.assign(new LeaseAgreementDocument(), m));
        }
        return [];
      }),
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }
}
